import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Input, Select, Form, Space, Tag, Modal, Typography, message } from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { customerApi } from '../services/api';
import { fmtCN } from '../utils/time';
import { isModuleReadOnly } from '../components/MainLayout';

const { Text } = Typography;

const LEVEL_OPTIONS = [
  { value: 'important', label: '重要客户' },
  { value: 'medium', label: '中等客户' },
  { value: 'normal', label: '一般普通客户' },
];
const LEVEL_COLOR = { important: 'red', medium: 'orange', normal: 'blue' };
const LEVEL_LABEL = { important: '重要客户', medium: '中等客户', normal: '一般普通客户' };

export default function Customers() {
  const isReadOnly = isModuleReadOnly('/customers');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [levelEdit, setLevelEdit] = useState(null);   // {id, name, level}
  const [levelSaving, setLevelSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchList = useCallback(async (kw) => {
    setLoading(true);
    try {
      const data = await customerApi.list({ keyword: kw || undefined });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(''); }, [fetchList]);

  const handleSearch = () => fetchList(keyword);

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      await customerApi.create(values);
      message.success(`客户「${values.name}」录入成功`);
      setCreateOpen(false);
      form.resetFields();
      fetchList(keyword);
    } catch (e) {} finally {
      setSubmitting(false);
    }
  };

  // 根据业务实际情况修改客户重要性等级（不允许删除客户）
  const handleLevelSave = async () => {
    if (!levelEdit?.level) { message.warning('请选择客户等级'); return; }
    setLevelSaving(true);
    try {
      const r = await customerApi.updateLevel(levelEdit.id, levelEdit.level);
      message.success(`客户「${r.name}」等级已更新为「${r.level_label}」`);
      setLevelEdit(null);
      fetchList(keyword);
    } catch (e) {} finally {
      setLevelSaving(false);
    }
  };

  const columns = [
    { title: '客户名称', dataIndex: 'name', key: 'name', render: (t) => <Text strong>{t}</Text> },
    {
      title: '客户等级',
      dataIndex: 'level',
      key: 'level',
      width: 160,
      render: (l) => <Tag color={LEVEL_COLOR[l] || 'default'}>{LEVEL_LABEL[l] || l}</Tag>,
    },
    {
      title: '录入时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (t) => (t ? fmtCN(t) : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, r) => (isReadOnly ? <span style={{ color: '#bbb' }}>只读</span> :
        <Button
          size="small"
          type="primary"
          ghost
          icon={<EditOutlined />}
          onClick={() => setLevelEdit({ id: r.id, name: r.name, level: r.level })}
        >
          修改等级
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>客户管理</h2>
          <p className="desc">调度人员只可录入客户（名称 + 三级等级）；客户不允许删减。客户等级用于优先级维度自动判定。</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchList(keyword)}>刷新</Button>
          {!isReadOnly && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateOpen(true); }}>
              录入客户
            </Button>
          )}
        </Space>
      </div>
      {isReadOnly && (
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 16px', marginBottom: 12, color: '#d48806', fontSize: 13 }}>
          🔒 当前账号对「客户管理」为只读模式，不可新增或修改客户
        </div>
      )}

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="按客户名称搜索"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            style={{ width: 280 }}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title="录入客户"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="保存"
        confirmLoading={submitting}
        width={440}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="客户名称"
            rules={[{ required: true, message: '请输入客户名称' }, { max: 200, message: '名称过长' }]}
          >
            <Input placeholder="客户名称" />
          </Form.Item>
          <Form.Item name="level" label="客户等级" initialValue="normal" rules={[{ required: true, message: '请选择客户等级' }]}>
            <Select options={LEVEL_OPTIONS} placeholder="选择客户等级" />
          </Form.Item>
          <Text type="secondary">客户录入后不可删除；重名会被拒绝。</Text>
        </Form>
      </Modal>

      <Modal
        title={`修改客户等级（${levelEdit?.name || ''}）`}
        open={!!levelEdit}
        onOk={handleLevelSave}
        onCancel={() => setLevelEdit(null)}
        okText="保存"
        confirmLoading={levelSaving}
        width={420}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Select
            value={levelEdit?.level}
            onChange={(v) => setLevelEdit({ ...levelEdit, level: v })}
            style={{ width: '100%' }}
            options={LEVEL_OPTIONS}
            placeholder="选择客户等级"
          />
          <Text type="secondary">客户等级用于优先级维度的自动判定；客户不可删除，仅可调整等级。</Text>
        </Space>
      </Modal>
    </div>
  );
}
